import { LitElement, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '@sdb-develop/learning-composition-player/src/store/store.js';
import { authenticate } from '@sdb-develop/learning-composition-player/src/store/auth/auth.js';
import { setTenant, clearTenant } from '@sdb-develop/learning-composition-player/src/store/auth/userManager.js'
import { GLOBAL_ID } from '@sdb-develop/learning-composition-player/src/store/env/env.js';
import { Routes } from '@lit-labs/router';
import './authentication/sdb-authenticate-callback.js';
import './authentication/sdb-authenticate-silent-renew.js';
import './sdb-player-app.js';
import './sdb-player-demo-app.js';
import '@sdb-develop/learning-composition-player/src/components/elearning/sdb-elearning-close-dialog.js';

@customElement('sdb-route')
class SdbRoute extends connect(store)(LitElement) {

    constructor() {
        super();

        this._matchingIdString = undefined;
        this._needsAuthentication = false;
        this._authenticated = false;

        this._routes = new Routes(this, [
            {
                path: '/silentrenew',
                render: () => html`<sdb-authenticate-silent-renew></sdb-authenticate-silent-renew>`,
            },
            {
                path: '/callback',
                render: () => html`<sdb-authenticate-callback @redirect=${(e)=> { this._goto(e.detail.path); }}></sdb-authenticate-callback>`,
            },
            {
                path: '/composition/:id',
                render: ({ id }) => {
                    const params = new URLSearchParams(window.location.search);

                    const keys = (params.get('keys') || '').split(',').filter(Boolean);
                    const sessionId = params.get('ses');
                    const portal = params.get('portal') || GLOBAL_ID;

                    return this._authenticatedRender(html`
                        <sdb-player-app
                            compositionId=${id}
                            sessionId=${sessionId}
                            .registrationKeys=${keys}
                            portal=${portal}>
                        </sdb-player-app>
                    `);
                },
                enter: async () => {
                    const params = new URLSearchParams(window.location.search);

                    this._matchIdString(params.get('idString'));
                    this._needsAuthentication = true;
                    clearTenant();
                },
            },
            { // legacy path
                path: '/composition/:id/:idString/:sessionId?',
                render: ({ id, sessionId }) => this._authenticatedRender(html`
                    <sdb-player-app
                        compositionId=${id}
                        sessionId=${sessionId}>
                    </sdb-player-app>
                `),
                enter: async ({ idString }) => {
                    this._needsAuthentication = true;
                    this._matchIdString(atob(idString));
                    clearTenant();
                },
            },
            {
                path: '/preview/:type/:id',
                render: ({ id, type }) => this._authenticatedRender(html`
                    <sdb-player-app
                        compositionId=${id}
                        type=${type}
                        preview>
                    </sdb-player-app>
                `),
                enter: async () => {
                    this._needsAuthentication = true;
                    clearTenant();
                },
            },
            {
                path: '/demo/:id/:idString?',
                render: ({ id, type, idString }) => this._authenticatedRender(html`
                    <sdb-player-demo-app
                        targetId=${id}
                        type=${type}
                        idString=${idString ? atob(idString) : undefined}
                        hideCompositionStatus>
                    </sdb-player-demo-app>
                `),
                enter: async () => {
                    this._needsAuthentication = true;
                    clearTenant();
                },
            },
            {
                path: '/embedded/:id/:tenant',
                render: ({ id }) => this._authenticatedRender(html`
                    <sdb-player-app compositionId=${id}></sdb-player-app>
                `),
                enter: async ({ tenant }) => {
                    this._needsAuthentication = true;
                    setTenant(tenant);
                },
            },
            {
                path: '/elearning-close-dialog',
                render: () => html`<sdb-elearning-close-dialog></sdb-elearning-close-dialog>`,
            },
            {
                path: '/*',
                render: () => html`<div style="margin: 20px;">unknown route</div>`,
            },
        ]);
    }

    render() {
        return html`<div>${this._routes.outlet()}</div>`;
    }

    stateChanged(state) {
        // The latch below is permanent once set, so an expired session would keep
        // rendering the authenticated view over a dead token. Release it first.
        if (this._authenticated && !state.auth.signedIn) {
            this._authenticated = false;
            this.requestUpdate();
        }

        if (state.oidc.isLoadingUser || state.auth.redirecting || !this._needsAuthentication || this._authenticated) {
            return;
        }

        if (state.auth.signedIn === true) {
            this._authenticated = true;
            this.requestUpdate();
        } else {
            store.dispatch(authenticate({ matchingIdString: this._matchingIdString }));
        }
    }

    _matchIdString(idString) {
        this._matchingIdString = idString;
    }

    async _goto(path) {
        // Split path and query/hash safely
        const url = new URL(path, window.location.origin);

        const pathname = url.pathname;
        const search = url.search;
        const hash = url.hash;

        // Restore query + hash without triggering a reload
        // Do this before router handling to ensure the full URL is correct for any route guards or effects
        window.history.replaceState(
            history.state,
            '',
            pathname + search + hash
        );

        // Let lit-router handle ONLY the path
        await this._routes.goto(pathname);
    }

    _authenticatedRender(authenticatedHtml) {
        return !this._authenticated ? html`<div class="spinner"></div>` : authenticatedHtml;
    }
}
